import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, Star } from 'lucide-react';
import { getIconComponent } from '@/components/homepage/iconMap';
import { cn } from '@/lib/utils';

interface FocusAreaCardProps {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  isPopular?: boolean;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
}

export const FocusAreaCard: React.FC<FocusAreaCardProps> = ({
  id,
  name,
  description,
  icon,
  isPopular,
  isSelected,
  onSelect,
}) => {
  const IconComponent = getIconComponent(icon);

  return (
    <TooltipProvider>
      <Card
        className={cn(
          "relative cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.01]",
          "border-2 group",
          isSelected 
            ? "border-primary bg-primary/5 shadow-elegant" 
            : "border-border hover:border-primary/50",
          isPopular && "ring-2 ring-accent/20"
        )}
        onClick={() => onSelect(!isSelected)}
      >
        {isPopular && (
          <div className="absolute -top-2 -right-2 z-10">
            <Badge variant="secondary" className="bg-accent text-accent-foreground gap-1 text-xs font-semibold">
              <Star className="w-3 h-3 fill-current" />
              Popular
            </Badge>
          </div>
        )}

        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-lg transition-colors",
              isSelected 
                ? "bg-primary text-primary-foreground" 
                : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
            )}>
              <IconComponent className="w-5 h-5" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className={cn(
                  "font-medium text-sm leading-tight transition-colors",
                  isSelected ? "text-primary" : "text-foreground"
                )}>
                  {name}
                </h3>
                
                {description && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="opacity-60 hover:opacity-100 transition-opacity">
                        <Info className="w-4 h-4 text-muted-foreground hover:text-primary" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">{description}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>

            {isSelected && (
              <div className="text-primary">
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};